import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import Marquee from "@/components/Marquee";
import StampQuote from "@/components/StampQuote";
import useSEO from "@/lib/useSEO";
import Reveal from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const MARQUEE_ITEMS = [
  "No human is illegal",
  "Make empathy great again",
  "Fight the regime",
  "ICE belongs in my drink",
  "Deport hate",
  "Racism is so last century",
  "Build bridges not walls",
  "What would Dolly do?",
  "Make America think again",
];

const CHAPTERS = [
  {
    n: "01",
    title: "Fabric First",
    body: "Unisex tees on 6.1 oz garment-dyed cotton, women's relaxed on 4.3 oz combed ring-spun, baby tees on 5.3 oz micro-rib, hoodies on 340 gsm heavyweight fleece. Fabric that outlasts news cycles, scandals, and most political careers.",
  },
  {
    n: "02",
    title: "Punch Up, Never Down",
    body: "Every joke on this site aims at power. Never at people. The government has lawyers — it can take a joke. That is the whole trick, and it is not negotiable.",
  },
  {
    n: "03",
    title: "Funny Beats Furious",
    body: "Anger burns out by Tuesday. A good joke gets quoted at Thanksgiving for a decade. We print the second one.",
  },
  {
    n: "04",
    title: "Wash Cold, Stay Hot",
    body: "Machine wash cold. Hang dry. Wear to family dinners. Results may vary. Opinions will not.",
  },
];

// Curated homepage mix: artwork + minimal text, black + white + natural colorways.
const HOME_MIX = [
  { slug: "make-empathy-great-again", color: "" },
  { slug: "ice-belongs-in-my-drink-hoodie", color: "" },
  { slug: "deport-hate", color: "" },
  { slug: "fight-the-regime-tee", color: "" },
  { slug: "be-kind-white-hoodie", color: "" },
  { slug: "what-would-dolly-do", color: "" },
];

// Women's Tees homepage section: first six in the owner-set display order,
// each card leading with its owner-set display color (card_color in catalog).
const WOMENS_HOME = [
  "no-kings-tee-womens",
  "bridges-not-walls-tee-womens",
  "make-empathy-great-again-womens",
  "deport-hate-tee-womens",
  "ice-belongs-in-my-drink-womens",
  "racism-is-so-last-century-womens",
];

const MaskedLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Home() {
  useSEO(
    "Fleece The Regime — Funny Heavyweight Protest Tees & Hoodies",
    "Funny, sharp anti-Trump, anti-ICE, anti-racism apparel: unisex heavyweight 6.1 oz tees, women's relaxed 4.3 oz tees, fitted baby tees, 340 gsm hoodies. Printed to order in the USA.",
    false,
    "/"
  );
  const [featured, setFeatured] = useState([]);
  const [womens, setWomens] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const heroRef = useRef(null);
  const spotRef = useRef(null);
  const [spotImgOk, setSpotImgOk] = useState(true);

  const { scrollYProgress: heroProg } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgWordY = useTransform(heroProg, [0, 1], [0, 220]);
  const heroTextY = useTransform(heroProg, [0, 1], [0, -80]);

  const { scrollYProgress: spotProg } = useScroll({ target: spotRef, offset: ["start end", "end start"] });
  const spotY = useTransform(spotProg, [0, 1], ["-10%", "10%"]);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => {
        const all = r.data;
        setTotalCount(all.length);
        const mixed = HOME_MIX.map(({ slug, color }) => {
          const p = all.find((x) => x.slug === slug);
          return p ? { product: p, colorOverride: color } : null;
        }).filter(Boolean);
        if (mixed.length) {
          setFeatured(mixed);
        } else {
          setFeatured(all.filter((p) => p.featured).slice(0, 4).map((p) => ({ product: p, colorOverride: "" })));
        }
        setWomens(WOMENS_HOME.map((slug) => all.find((x) => x.slug === slug)).filter(Boolean));
      })
      .catch(() => {});
  }, []);

  const spotProduct = (featured.find((f) => f.product.type === "hoodie") || featured[0] || {}).product;
  const bento = ["sm:col-span-7", "sm:col-span-5", "sm:col-span-5", "sm:col-span-7", "sm:col-span-6", "sm:col-span-6"];

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-start overflow-hidden border-b-2 border-black pt-32 sm:pt-36 pb-16">
        <motion.span
          aria-hidden
          style={{ y: bgWordY }}
          className="pointer-events-none select-none absolute -right-10 top-6 font-display uppercase text-[26vw] leading-none text-outline opacity-25"
        >
          Resist
        </motion.span>

        <motion.div style={{ y: heroTextY }} className="relative z-10 px-6 sm:px-12 lg:px-20 max-w-[90rem] w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="font-mono text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-primary mb-8"
            data-testid="hero-eyebrow"
          >
            Est. 2026 — Heavyweight Protestwear
          </motion.p>

          <h1 className="font-display uppercase text-[15vw] sm:text-[12vw] lg:text-[9vw] leading-[0.85] tracking-tighter" data-testid="hero-headline">
            <MaskedLine i={0}>Heavy</MaskedLine>
            <MaskedLine i={1}>Cotton.</MaskedLine>
            <MaskedLine i={2} className="text-primary">Heavier</MaskedLine>
            <MaskedLine i={3}>Opinions.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-10 max-w-xl font-mono text-sm sm:text-base leading-relaxed text-black/70"
          >
            Funny, sharp protest apparel for people who read the news and refuse to be quiet about it.
            Unisex heavyweight, women's relaxed, fitted baby tees, and 340 gsm hoodies. Premium fabric. Zero fascists. Some assembly required at Thanksgiving.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/shop?type=womens-tee"
              data-testid="hero-shop-button"
              className="inline-flex items-center gap-3 border-2 border-black bg-primary px-8 py-4 font-display uppercase text-xl text-white shadow-hard btn-press hover:bg-black"
            >
              Shop women's tees <ArrowRight size={20} />
            </Link>
            <Link
              to="/shop"
              data-testid="hero-shop-all-button"
              className="inline-flex items-center gap-3 border-2 border-black bg-background px-8 py-4 font-display uppercase text-xl shadow-hard btn-press hover:bg-accent"
            >
              Shop all <ArrowRight size={20} />
            </Link>
            <button
              onClick={() => document.getElementById("manifesto")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-manifesto-button"
              className="inline-flex items-center gap-3 border-2 border-black bg-background px-8 py-4 font-display uppercase text-xl shadow-hard btn-press hover:bg-accent"
            >
              Read the manifesto <ArrowDown size={20} />
            </button>
            <span
              data-testid="hero-quality-stamp"
              className="inline-flex items-center gap-2 border-2 border-black bg-accent px-5 py-4 font-mono text-xs font-bold uppercase tracking-[0.18em] shadow-hard -rotate-1"
            >
              ★ 100% Fascist-Free Cotton · Est. 2026
            </span>
          </motion.div>
        </motion.div>
      </section>

      <Marquee items={MARQUEE_ITEMS} className="bg-primary text-black" />

      {/* FEATURED DROP */}
      <section className="px-6 sm:px-12 lg:px-20 py-24 sm:py-32">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.85] tracking-tighter" data-testid="featured-heading">
              The<br />Current<br /><span className="text-primary">Drop</span>
            </h2>
            <p className="font-mono text-sm max-w-xs text-black/60 leading-relaxed">
              Wearable dissent in tees, hoodies and fitted baby tees. Printed to order.
            </p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-8 sm:gap-10">
          {featured.map(({ product, colorOverride }, i) => (
            <ProductCard key={product.id} product={product} index={i} className={bento[i % bento.length]} colorOverride={colorOverride} />
          ))}
        </div>
        <Reveal delay={0.1} className="mt-14">
          <Link
            to="/shop"
            data-testid="featured-view-all"
            className="inline-flex items-center gap-3 border-2 border-black bg-black px-8 py-4 font-display uppercase text-xl text-background shadow-hard-red btn-press"
          >
            {totalCount > 0 ? `View all ${totalCount} pieces` : "View all pieces"} <ArrowRight size={20} />
          </Link>
        </Reveal>
      </section>

      {/* WOMEN'S TEES */}
      {womens.length > 0 && (
        <section className="border-t-2 border-black bg-[#F4F4F0] px-6 sm:px-12 lg:px-20 py-24 sm:py-32" data-testid="womens-home-section">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
              <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.85] tracking-tighter" data-testid="womens-home-heading">
                Women's<br />Relaxed<span className="text-primary">.</span>
              </h2>
              <p className="font-mono text-sm max-w-xs text-black/60 leading-relaxed">
                The same slogans, finally in your fit: the Next Level boyfriend cut, made for women. XS to 2XL.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-12">
            {womens.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>
          <Reveal delay={0.1} className="mt-14">
            <Link
              to="/shop?type=womens-tee"
              data-testid="womens-view-all"
              className="inline-flex items-center gap-3 border-2 border-black bg-black px-8 py-4 font-display uppercase text-xl text-background shadow-hard-red btn-press"
            >
              View all women's tees <ArrowRight size={20} />
            </Link>
          </Reveal>
        </section>
      )}

      {/* MANIFESTO */}
      <StampQuote text="Anger burns out by Tuesday. A good joke gets quoted at Thanksgiving for a decade." />

      <section id="manifesto" className="border-t-2 border-black bg-[#111] text-background px-6 sm:px-12 lg:px-20 py-24 sm:py-32">        <Reveal>
          <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary mb-6">The Manifesto</p>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.85] tracking-tighter mb-20" data-testid="manifesto-heading">
            Four things<br />we actually<br />believe.
          </h2>
        </Reveal>
        <div className="space-y-0">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div
                data-testid={`manifesto-chapter-${c.n}`}
                className={`flex flex-col sm:flex-row gap-6 sm:gap-14 border-t-2 border-white/25 py-12 ${i % 2 === 1 ? "sm:flex-row-reverse sm:text-right" : ""}`}
              >
                <span className="font-display text-7xl sm:text-8xl leading-none text-primary shrink-0">{c.n}</span>
                <div className={i % 2 === 1 ? "sm:ml-auto" : ""}>
                  <h3 className="font-display uppercase text-3xl sm:text-4xl tracking-tight">{c.title}</h3>
                  <p className="mt-4 max-w-md font-mono text-sm leading-relaxed text-white/60">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SPOTLIGHT */}
      {spotProduct && (
        <section ref={spotRef} className="relative bg-black text-background overflow-hidden border-y-2 border-black">
          <div className="grid lg:grid-cols-2">
            <div className="px-6 sm:px-12 lg:px-20 py-24 sm:py-32 flex flex-col justify-center relative z-10">
              <Reveal>
                <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary mb-6">Spotlight № 03</p>
                <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.85] tracking-tighter" data-testid="spotlight-heading">
                  The uniform of the ungovernable.
                </h2>
                <p className="mt-8 max-w-md font-mono text-sm leading-relaxed text-white/60">
                  {spotProduct.description}
                </p>
                <Link
                  to={`/product/${spotProduct.slug}`}
                  data-testid="spotlight-cta"
                  className="mt-10 inline-flex w-fit items-center gap-3 border-2 border-background bg-background px-8 py-4 font-display uppercase text-xl text-black shadow-[6px_6px_0_0_#FF4D00] btn-press hover:bg-accent hover:border-black"
                >
                  {spotProduct.name} — ${spotProduct.price.toFixed(2)} <ArrowRight size={20} />
                </Link>
              </Reveal>
            </div>
            <div className="relative min-h-[60vh] lg:min-h-[80vh] border-l-2 border-black lg:border-background/20 overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center bg-[#111] p-10">
                <span className="font-display uppercase text-center text-4xl sm:text-6xl leading-[0.95] text-[#F4F4F0]">
                  {spotProduct.slogan}
                </span>
              </div>
              {spotImgOk && (
                <motion.img
                  src={imgUrl(spotProduct.model_image || spotProduct.image)}
                  alt={spotProduct.name}
                  onError={() => setSpotImgOk(false)}
                  style={{ y: spotY }}
                  className="absolute inset-0 h-[120%] w-full object-cover object-top"
                />
              )}
            </div>
          </div>
        </section>
      )}

      <Marquee items={MARQUEE_ITEMS} slow separator="✦" className="bg-accent text-black" />

      {/* CTA BAND */}
      <section className="px-6 sm:px-12 lg:px-20 py-24 sm:py-32 border-b-2 border-black">
        <Reveal className="text-left">
          <h2 className="font-display uppercase text-[11vw] sm:text-[8vw] leading-[0.85] tracking-tighter" data-testid="cta-heading">
            Grab one before<br />they're <span className="text-primary">banned.</span>
          </h2>
          <p className="mt-8 max-w-lg font-mono text-sm leading-relaxed text-black/60">
            Kidding. Probably. Either way, the specs are published and the jokes are load-bearing.
          </p>
          <Link
            to="/shop"
            data-testid="cta-shop-button"
            className="mt-10 inline-flex items-center gap-3 border-2 border-black bg-primary px-10 py-5 font-display uppercase text-2xl text-white shadow-hard btn-press hover:bg-black"
          >
            Shop everything <ArrowRight size={22} />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
